import { makeObservable } from 'mobx';
import compact from 'lodash/compact';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';

import { ReservationData, ReservationStatus } from 'chaika-frontend-types/frontend-cms';
import { ReservationType, OnlineVisitChannel } from 'chaika-enums';

import { SimpleList } from '@store/models/SimpleList';
import { translate } from '@chaika/i18n/helper';
import { DateType } from '@utils/DateHelper';

export interface ReservationFilterParams {
    types?: ReservationType[];
    referralIds?: string[];
    authorId?: string[];
    patientId?: string[];
    serviceIds?: string[];
    date?: DateType;
    from?: DateType;
    to?: DateType;
    statuses?: ReservationStatus[];
}

export const onlineVisitOption = [
    {
        value: OnlineVisitChannel.Zoom,
        label: 'meet',
    },
    {
        value: OnlineVisitChannel.Phone,
        label: translate('phone'),
    },
];

export const onlineVisitWithSkypeOption = [
    ...onlineVisitOption,
    {
        value: OnlineVisitChannel.Skype,
        label: 'skype',
    },
];

export class ReservationList<T extends ReservationData = ReservationData> extends SimpleList<
    T,
    ReservationFilterParams
> {
    constructor(params?: T[]) {
        super(params);
        makeObservable(this);
    }

    public getPatientIds(): string[] {
        return uniq(compact(this.values.map(reservation => reservation.patientId)));
    }

    public getServiceIds(): string[] {
        return uniq(compact(flatten(this.values.map(reservation => reservation.serviceIds))));
    }

    public getAuthorIds(): string[] {
        return uniq(compact(this.values.map(reservation => reservation.authorId)));
    }

    protected filterValue(
        value: T,
        { types, referralIds, date, from, to, statuses, authorId, serviceIds, patientId }: ReservationFilterParams,
    ): boolean {
        return (
            this.filterFieldValueByArray(value, types, 'type') &&
            this.filterFieldValueByArray(value, statuses, 'status') &&
            this.filterFieldValueBySameDay(value, date, 'startTime') &&
            this.filterFieldValueBySameOrAfterDate(value, from, 'startTime') &&
            this.filterFieldValueBySameOrBeforeDate(value, to, 'endTime') &&
            this.filterFieldValueByArray(value, referralIds, 'referralId') &&
            this.filterFieldValueByArray(value, authorId, 'authorId') &&
            this.filterFieldValueByArray(value, patientId, 'patientId') &&
            this.filterArrayFieldValueByArray(value, serviceIds, 'serviceIds')
        );
    }
}
